import React, { Dispatch, SetStateAction } from "react"
import { useAppDispatch } from "lib/store"
import { Inventory } from "lib/types/generated/graphql-types"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"

import { Container, EmptyList, Id, Name, Wrapper } from "./InventoryTable.styled"

import TableRow from "components/Ui/Table/TableRow"
import ErrorMessage from "components/Ui/Messages/ErrorMessage"
import TableBlurLoading from "../Ui/Table/TableBlurLoading"

import { ReactComponent as WarehouseIcon } from "images/icons/warehouse.svg"
import { ReactComponent as StoreIcon } from "images/icons/store.svg"
import { ReactComponent as DropShipIcon } from "images/icons/truck-arrow-right.svg"
import { ReactComponent as InboundIcon } from "images/icons/reply-clock.svg"
import TableHeader from "../Ui/Table/TableHeader"
import { Tooltip } from "../Ui/Tooltip"

type Props = {
  inventories: Inventory[]
  loading: boolean
  error: object | undefined
  setEditId: Dispatch<SetStateAction<string>>
}

export const InventoryTable = ({ inventories, loading = false, setEditId, error }: Props) => {
  const dispatch = useAppDispatch()

  const tableHeader = (
    <TableHeader iconHeader={true}>
      <div>Name</div>
      <div className="hideOnSmallScreens">Description</div>
    </TableHeader>
  )

  const getIcon = (type: string) => {
    if (type === "STORE")
      return (
        <StoreIcon
          data-tooltip-id="tooltip"
          data-tooltip-content={"Inventory type: Store"}
          data-tooltip-delay-hide={100}
        />
      )
    if (type === "DROP_SHIP")
      return (
        <DropShipIcon
          data-tooltip-id="tooltip"
          data-tooltip-content={"Inventory type: Drop ship"}
          data-tooltip-delay-hide={100}
        />
      )
    if (type === "INBOUND")
      return (
        <InboundIcon
          data-tooltip-id="tooltip"
          data-tooltip-content={"Inventory type: Inbound"}
          data-tooltip-delay-hide={100}
        />
      )
    return (
      <WarehouseIcon
        data-tooltip-id="tooltip"
        data-tooltip-content={"Inventory type: Warehouse"}
        data-tooltip-delay-hide={100}
      />
    )
  }

  const getTableRow = (inventory?: Inventory) => {
    if (inventory) {
      return (
        <TableRow
          key={inventory.id}
          handleOnClick={() => {
            setEditId(inventory.id)
            dispatch(showEditSidebar())
          }}
        >
          <Tooltip id="tooltip" />
          {getIcon(inventory?.type ?? "")}
          <div>
            <div>
              <Name>{inventory.name}</Name>
              <Id>{inventory.id}</Id>
            </div>
          </div>
          <div className="hideOnSmallScreens">
            <p>{inventory.description}</p>
          </div>
        </TableRow>
      )
    }
    return (
      <TableRow>
        <WarehouseIcon />
        <div>
          <div>
            <Name>Inventory</Name>
            <Id>inventoryid</Id>
          </div>
        </div>
        <div className="hideOnSmallScreens">
          <p>Inventory description</p>
        </div>
      </TableRow>
    )
  }

  if (loading)
    return <TableBlurLoading numberOfRows={2} tableHeader={tableHeader} rows={getTableRow()} />

  return (
    <Wrapper>
      <Container>
        {error && (
          <ErrorMessage
            showRefreshButton
            message={
              <>
                Error loading Inventories. Try refreshing the page, or contact{" "}
                <a href="mailto:support@brinkcommerce.com">support</a>.
              </>
            }
          />
        )}
        {inventories.length > 0 ? (
          <>
            {tableHeader}
            {[...inventories].map((inventory) => getTableRow(inventory))}
          </>
        ) : (
          <EmptyList>
            <WarehouseIcon />
            <p> No inventories found</p>
          </EmptyList>
        )}
      </Container>
    </Wrapper>
  )
}
